import { useCallback, useState } from "react";
import { api, ApiError, type RedeemedAccount } from "../lib/api";
import { Button } from "../components/primitives";
import { BrandLockup } from "../components/BrandLockup";
import { QrCode } from "../components/QrCode";
import { originFor, surfaceForHost } from "../lib/surface";

/**
 * Setting up an invited account.
 *
 * The only screen in the platform that renders for someone with no session,
 * because the person reading it does not have an account until they press the
 * button. What stands in for authority is the token.
 *
 * **The token arrives in the URL fragment, not the path.** `/invite#<token>`
 * rather than `/invite/<token>`, because a fragment is never transmitted to a
 * server: it stays out of nginx's access log, out of any proxy in front of it,
 * and out of the referrer header. The path form would have written a live
 * credential to disk in plain text on the first click. It is read here with
 * `location.hash` and posted in a request body.
 *
 * Redemption is deliberately a button and not something that happens on load.
 * A link preview, a mail scanner or a prefetch would otherwise spend the
 * invitation before the person ever saw it, and a spent invitation cannot be
 * un-spent — they would have to ask for another with no idea why.
 *
 * What is shown afterwards is shown **once**. The password is generated and
 * never stored in the clear, and the TOTP secret is never retrievable again.
 * Leaving this page without copying both means asking an administrator to
 * revoke and reissue.
 */
export function Invite() {
  const [account, setAccount] = useState<RedeemedAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Strip the leading '#'. Empty means somebody typed /invite by hand.
  const token = decodeURIComponent(window.location.hash.replace(/^#/, ""));

  const redeem = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<RedeemedAccount>("/api/invitations/redeem", {
        token,
      });
      setAccount(result);
      // Remove the token from the address bar the moment it is spent, so it
      // does not sit in browser history or get copied out of the URL bar.
      window.history.replaceState(null, "", window.location.pathname);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Something went wrong setting up your account.",
      );
    } finally {
      setBusy(false);
    }
  }, [token]);

  return (
    <div
      data-world="ink"
      className="flex min-h-screen flex-col bg-canvas text-ink"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-6 py-12">
        <BrandLockup size="large" />

        {!account && (
          <div className="mt-10 border-t border-line-strong pt-8">
            <h1 className="font-editorial text-3xl text-ink">
              You have been invited
            </h1>

            {!token && (
              <p className="mt-4 max-w-[52ch] text-base text-secondary">
                This link is incomplete. Use the full link exactly as it was
                sent to you — if it was broken across two lines in a message,
                the end of it is missing.
              </p>
            )}

            {token && !error && (
              <>
                <p className="mt-4 max-w-[52ch] text-base text-secondary">
                  Setting up your account will show your password and a code to
                  scan with your authenticator app. They are shown{" "}
                  <strong className="text-ink">once only</strong> and cannot be
                  recovered afterwards, so do this when you have somewhere to
                  save them.
                </p>
                <Button
                  tone="primary"
                  className="mt-7"
                  loading={busy}
                  onClick={() => void redeem()}
                >
                  Set up my account
                </Button>
              </>
            )}

            {error && (
              <div className="mt-6 border-l-2 border-danger pl-4">
                <p className="text-base text-ink">{error}</p>
                <p className="mt-2 max-w-[52ch] text-sm text-secondary">
                  Invitations last seven days and work only once. Ask whoever
                  invited you to send a new one.
                </p>
              </div>
            )}
          </div>
        )}

        {account && <Credentials account={account} />}
      </div>
    </div>
  );
}

function Credentials({ account }: { account: RedeemedAccount }) {
  // Where this person actually signs in. A client belongs at the portal
  // hostname and everyone else at the firm's, and sending them to the wrong
  // one would greet them with the "wrong door" screen on their first visit.
  const target =
    surfaceForHost() === "either"
      ? window.location.origin
      : originFor(account.role === "client" ? "client" : "firm");

  return (
    <div className="mt-10 border-t border-line-strong pt-8">
      <h1 className="font-editorial text-3xl text-ink">
        Your account is ready, {account.fullName.split(" ")[0]}
      </h1>

      <p className="mt-4 max-w-[54ch] text-base text-secondary">
        Save these now. This is the only time they are shown — the password is
        not stored anywhere it can be read back, and the key cannot be shown
        again.
      </p>

      <dl className="mt-8 flex flex-col gap-6">
        <Secret label="Email" value={account.email} />
        <Secret label="Password" value={account.password} />
        <Secret label="Authenticator key" value={account.mfaSecret} />
      </dl>

      <div className="mt-9 border-t border-line pt-6">
        <p className="label">Add this to an authenticator</p>

        <div className="mt-5 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <QrCode
            value={account.otpauth}
            label={`QR code enrolling ${account.email} in an authenticator app`}
            className="shrink-0"
          />

          <div className="min-w-0">
            <p className="max-w-[48ch] text-sm text-secondary">
              Open Google Authenticator, Authy or 1Password, choose to add an
              account, and point the camera at this code. You will need the
              six-digit code it generates every time you sign in — it is not
              optional.
            </p>
            <p className="mt-4 max-w-[48ch] text-sm text-secondary">
              Reading this on the same phone the authenticator is on? There is
              nothing to point a camera at, so choose to enter a setup key by
              hand instead, and use the authenticator key above.
            </p>
          </div>
        </div>
      </div>

      <a
        href={target}
        className="mt-9 inline-flex min-h-11 items-center justify-center border border-ink bg-ink px-5 text-sm font-medium text-canvas transition-colors duration-200 hover:bg-ink/85"
      >
        Go to sign in
      </a>
    </div>
  );
}

/**
 * One secret, with a copy button. Selectable text as well as a button, because
 * clipboard access is refused often enough — an insecure origin, a locked-down
 * browser — that a copy button alone would strand someone with no way to get
 * the value out.
 */
function Secret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Selecting the text by hand still works; say nothing rather than
      // raise an error the person can do nothing about.
    }
  };

  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
      <div className="min-w-0">
        <dt className="label">{label}</dt>
        <dd className="mt-2 select-all break-all font-mono text-base text-ink">
          {value}
        </dd>
      </div>
      <Button tone="secondary" onClick={() => void copy()}>
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
